import {
  Component,
  Input,
  OnDestroy,
  OnInit,
  AfterViewInit,
  ViewChild,
  ElementRef,
} from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { BookType, parseBukURL } from '@bukio/viewer';

import { APIError } from 'shared/services';
import { ToastService } from 'shared/ui';

import { Book } from '../../models/book.model';
import { BookmarksService } from '../../services/bookmarks.service';

import { AnnotationListComponent } from '../annotation-list/annotation-list.component';

import { Bookmark } from '../../models/bookmark.model';

export interface AnnotationView extends Bookmark {
  thumbnail?: string;
  page?: number;
}
@Component({
  selector: 'viewer-bookmark-list',
  templateUrl: './bookmark-list.component.html',
  styleUrls: [
    '../annotation-list/annotation-list.component.scss',
    './bookmark-list.component.scss',
  ],
})
export class BookmarkListComponent
  extends AnnotationListComponent<AnnotationView>
  implements OnInit, OnDestroy, AfterViewInit
{
  @Input() book!: Book;

  @ViewChild('listContainer', { static: false })
  listContainer?: ElementRef;

  private _unsubscriber = new Subject<void>();
  private _scrollTimeout?: any; // 스크롤 타이머 추가

  constructor(
    private _toastService: ToastService,
    private _bookmarksService: BookmarksService
  ) {
    super();
  }

  override ngOnInit(): void {
    super.ngOnInit();

    // 북마크 목록 변경을 구독하여 자동으로 업데이트
    this._bookmarksService.bookmarks$
      .pipe(takeUntil(this._unsubscriber))
      .subscribe(() => {
        this.loadItems();
      });
  }

  ngAfterViewInit(): void {
    if (this.listContainer) {
      this.listContainer.nativeElement.addEventListener('scroll', () => {
        // 스크롤 중일 때 클래스 추가
        this.listContainer!.nativeElement.classList.add('is-scrolling');

        // 기존 타이머가 있으면 제거
        if (this._scrollTimeout) {
          clearTimeout(this._scrollTimeout);
        }

        // 스크롤이 멈춘 후 300ms 후에 클래스 제거
        this._scrollTimeout = setTimeout(() => {
          this.listContainer!.nativeElement.classList.remove('is-scrolling');
        }, 300);
      });
    }
  }

  ngOnDestroy(): void {
    // 타이머 정리
    if (this._scrollTimeout) {
      clearTimeout(this._scrollTimeout);
    }

    this._unsubscriber.next();
    this._unsubscriber.complete();
  }

  public override loadItems(): void {
    this._items = this._bookmarksService.bookmarks.map((bookmark) => {
      const address = parseBukURL(bookmark.url);

      return {
        ...bookmark,
        selected: false,
        page:
          this.book.meta.type === BookType.PDF
            ? this.book.getIndexOfItem(address.iid) + 1
            : undefined,
        thumbnail: this.book.getItem(address.iid)?.thumbnail,
        updatedDateTime: new Date(bookmark.createdDate).getTime(),
      };
    });

    this._sortItems();
  }

  _onDeleteButtonClick(bookmark: AnnotationView): void {
    this._deleteItems([bookmark.id]);
  }

  _onDeleteSelectedItemsButtonClick(): void {
    const ids = this._getSelectedItems().map((item) => item.id);
    this._deleteItems(ids);
  }

  _onItemClick(item: AnnotationView): void {
    this.itemClicked.emit(item.url);
  }

  private _deleteItems(ids: number[]): void {
    this._bookmarksService.removeById(ids).subscribe({
      next: () => {
        this.loadItems();
        this._toastService.open('북마크가 삭제되었습니다.');
      },
      error: (error) => {
        if (error instanceof HttpErrorResponse) {
          APIError.createGeneralErrorMessage(error.status);
        }
      },
    });
  }
}
